import * as React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const { t } = useTranslation();
  // Add custom navigation to enable year switching
  const today = new Date();
  const getInitialMonth = () => {
    if (props.month) return props.month;
    if ((props as any).selected instanceof Date) return (props as any).selected;
    return today;
  };

  const [displayedMonth, setDisplayedMonth] = React.useState<Date | undefined>(
    getInitialMonth()
  );

  // Move year backward
  const handlePreviousYear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (displayedMonth) {
      const newMonth = new Date(displayedMonth);
      newMonth.setFullYear(displayedMonth.getFullYear() - 1);
      setDisplayedMonth(newMonth);
      props.onMonthChange?.(newMonth);
    }
  };

  // Move year forward
  const handleNextYear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (displayedMonth) {
      const newMonth = new Date(displayedMonth);
      newMonth.setFullYear(displayedMonth.getFullYear() + 1);
      setDisplayedMonth(newMonth);
      props.onMonthChange?.(newMonth);
    }
  };

  // Keep displayedMonth in sync with external month changes
  React.useEffect(() => {
    if (props.month) {
      setDisplayedMonth(props.month);
    }
  }, [props.month]);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      month={displayedMonth}
      onMonthChange={setDisplayedMonth}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-2',
        month: 'flex flex-col gap-4',
        caption: 'flex justify-center pt-1 relative items-center w-full',
        caption_label: 'text-sm font-medium',
        nav: 'flex items-center gap-1',
        nav_button: cn(
          buttonVariants({ variant: 'outline' }),
          'size-7 bg-transparent p-0 opacity-50 hover:opacity-100'
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex gap-0.5 sm:gap-1',
        head_cell: 'text-muted-foreground rounded-md w-9 sm:w-10 font-normal text-xs sm:text-sm',
        row: 'flex w-full mt-2 gap-0.5 sm:gap-1',
        cell: cn(
          'relative p-0 text-center text-base focus-within:relative focus-within:z-20',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            : '[&:has([aria-selected])]:rounded-md'
        ),
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 sm:h-10 sm:w-12 p-0 text-sm sm:text-base font-normal aria-selected:opacity-100 hover:bg-primary hover:text-white transition-colors'
        ),
        day_range_start:
          'day-range-start aria-selected:bg-wb-sienna aria-selected:text-white',
        day_range_end:
          'day-range-end aria-selected:bg-wb-sienna aria-selected:text-white',
        day_selected:
          'bg-secondary text-white hover:bg-secondary hover:text-white focus:bg-secondary focus:text-white',
        day_today: 'bg-accent text-accent-foreground',
        day_outside:
          'day-outside text-muted-foreground aria-selected:text-muted-foreground',
        day_disabled: 'text-muted-foreground opacity-50',
        day_range_middle:
          'aria-selected:bg-accent aria-selected:text-accent-foreground',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn('size-4', className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn('size-4', className)} {...props} />
        ),
        Caption: ({ displayMonth, ...props }) => (
          <div className="flex justify-between items-center w-full">
            <div className="flex gap-1">
              <button
                className={cn(
                  buttonVariants({ variant: 'outline' }),
                  'size-7 bg-transparent p-0 opacity-50 hover:opacity-100'
                )}
                onClick={handlePreviousYear}
                title={t('calendar.previousYear')}
              >
                <ChevronsLeft className="size-4" />
              </button>
              <button
                className={cn(
                  buttonVariants({ variant: 'outline' }),
                  'size-7 bg-transparent p-0 opacity-50 hover:opacity-100'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (displayedMonth) {
                    const newMonth = new Date(displayedMonth);
                    newMonth.setMonth(displayedMonth.getMonth() - 1);
                    setDisplayedMonth(newMonth);
                  }
                }}
                title={t('calendar.previousMonth')}
              >
                <ChevronLeft className="size-4" />
              </button>
            </div>

            <div className="text-lg font-medium">
              {displayMonth.toLocaleDateString('en-US', {
                month: 'long',
                year: 'numeric',
              })}
            </div>

            <div className="flex gap-1">
              <button
                className={cn(
                  buttonVariants({ variant: 'outline' }),
                  'size-7 bg-transparent p-0 opacity-50 hover:opacity-100'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (displayedMonth) {
                    const newMonth = new Date(displayedMonth);
                    newMonth.setMonth(displayedMonth.getMonth() + 1);
                    setDisplayedMonth(newMonth);
                  }
                }}
                title={t('calendar.nextMonth')}
              >
                <ChevronRight className="size-4" />
              </button>
              <button
                className={cn(
                  buttonVariants({ variant: 'outline' }),
                  'size-7 bg-transparent p-0 opacity-50 hover:opacity-100'
                )}
                onClick={handleNextYear}
                title={t('calendar.nextYear')}
              >
                <ChevronsRight className="size-4" />
              </button>
            </div>
          </div>
        ),
      } as any}
      {...props}
      captionLayout={"dropdown" as any}
    />
  );
}

export { Calendar };
